'use client';

import React from 'react';

import { Modal, type ModalProps } from '@depop/web-ui-kit/Modal';
import { RadioButtons } from '@depop/web-ui-kit/RadioButtons';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { ShopSortByValues, isShopSort } from '@/modules/search/types';
import { useSearchFilters } from '@/modules/search/useSearchFilters';

type Props = Pick<ModalProps, 'isOpen' | 'setOpen'> & {
  options: Array<{
    id: ShopSortByValues;
    name: string;
  }>;
};

export function SortByModal({ isOpen, setOpen, options }: Props) {
  const {
    searchFilters,
    setSearchFilters: { setSortFilter },
  } = useSearchFilters();
  const tCommon = useTranslations('common');
  const tSearch = useTranslations('search');
  const pathname = usePathname();

  function handleSortChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;

    if (!isShopSort(value)) {
      return;
    }

    if (value === ShopSortByValues.SellersRecommendations) {
      // If no filter has been applied and the user clicks "Relevance", then we should not add the sort query param to the URL.
      // In this scenario, the "Relevance" option will order the products by the seller's manual sort order.
      return window.history.pushState(null, '', pathname);
    }
    return setSortFilter(value);
  }

  return (
    <Modal
      isOpen={isOpen}
      setOpen={setOpen}
      title={tCommon('Sorting.SortBy')}
      content={() => (
        <>
          <div className={styles.radioButtonsWrapper}>
            <RadioButtons
              name="sort"
              activeValue={searchFilters.sort || options[0].id}
              onChange={handleSortChange}
              className={styles.radioButtons}
              options={options.map(({ id, name }) => ({
                id,
                value: id,
                labelText: tCommon(name),
              }))}
            />
          </div>
          <div className={styles.viewItemsWrapper}>
            <ButtonExperiment fullWidth onClick={() => setOpen(false)}>
              {tSearch('ViewItems')}
            </ButtonExperiment>
          </div>
        </>
      )}
    />
  );
}
